/**
 * MessageActions
 *
 * Copy and download-as-PDF controls for a single assistant message.
 *
 * Key responsibilities:
 * - Copy the message's raw markdown source to the clipboard (what someone
 *   pastes into a doc or a note, rather than the flattened rendered text)
 * - Download the rendered message as a PDF file, via html2pdf
 * - Show transient feedback: a checkmark on a successful copy, a spinner while
 *   the PDF renders, and an explicit failure state if it does not
 *
 * Always visible rather than hover-revealed: the primary surface here is a
 * phone, where there is no hover state to reveal anything.
 *
 * The PDF button reports failure rather than failing silently. Rendering runs
 * html2canvas over the message and can take a second or more on a phone, so a
 * button that did nothing visible would read as broken.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { Copy, Check, FileDown, Loader2, AlertCircle } from "lucide-react";
import { exportElementToPdf } from "@/lib/exportPdf";

/** How long transient states (copied / failed) stay on screen. */
const FEEDBACK_MS = 2000;

type PdfState = "idle" | "working" | "failed";

interface MessageActionsProps {
  /** Raw markdown source — this is what lands on the clipboard. */
  content: string;
  /**
   * The rendered message node. Its HTML is what gets captured, so the PDF
   * matches what is on screen, tables included.
   */
  contentRef: RefObject<HTMLElement>;
  /** Used to build the download filename. */
  documentTitle?: string;
}

export function MessageActions({
  content,
  contentRef,
  documentTitle = "AttuneWell conversation",
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [pdfState, setPdfState] = useState<PdfState>("idle");

  /*
    The export is async and the message list re-renders as new messages stream
    in, so the component can unmount mid-render. Without this guard the
    completion handler sets state on an unmounted component.
  */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (pdfState !== "failed") return;
    const timer = setTimeout(() => setPdfState("idle"), FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [pdfState]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
    } catch {
      // Permission denied or a rejected write. Nothing actionable to say —
      // swallow it rather than leaving an unhandled rejection.
    }
  }, [content]);

  const handleDownloadPdf = useCallback(async () => {
    // Double-tap while rendering would start a second canvas capture.
    if (pdfState === "working") return;

    setPdfState("working");
    const ok = await exportElementToPdf(contentRef.current, documentTitle);
    if (!mounted.current) return;
    setPdfState(ok ? "idle" : "failed");
  }, [contentRef, documentTitle, pdfState]);

  // Insecure contexts (and older iOS) have no clipboard API at all.
  const canCopy =
    typeof navigator !== "undefined" && Boolean(navigator.clipboard);

  const buttonClass =
    "inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:cursor-default disabled:opacity-70";

  return (
    <div
      className="mt-2 flex items-center gap-1 border-t border-border/40 pt-1.5"
      data-print-hide
    >
      {canCopy && (
        <button
          type="button"
          onClick={handleCopy}
          className={buttonClass}
          aria-label={copied ? "Copied" : "Copy message"}
          data-testid="copy-message-button"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {copied ? "Copied" : "Copy"}
        </button>
      )}
      <button
        type="button"
        onClick={handleDownloadPdf}
        disabled={pdfState === "working"}
        className={buttonClass}
        aria-label={
          pdfState === "working"
            ? "Preparing PDF"
            : pdfState === "failed"
              ? "PDF failed, try again"
              : "Download as PDF"
        }
        aria-busy={pdfState === "working"}
        data-testid="print-message-button"
      >
        {pdfState === "working" ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : pdfState === "failed" ? (
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <FileDown className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {pdfState === "working"
          ? "PDF…"
          : pdfState === "failed"
            ? "Failed"
            : "PDF"}
      </button>
    </div>
  );
}
